import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { RecordManagement } from "app/class/record-management";
import SysRecord from "app/class/record";
import { evaluateCondition } from "shared/services/conditionEvaluatorForActions";
import queryKeys from "shared/services/queryKeys";
import { RecordActionConditionContext } from "../types/record-producer-actions";
import { ClientScriptEventType } from "../services/clientScriptRuntime";

const useClientScripts = (
    tableId: string | undefined,
    context: RecordActionConditionContext,
    enabled = true
) => {
    const { data: scripts = [], isLoading } = useQuery({
        queryKey: queryKeys.records.config("sys_client_script", { for_table: tableId, active: true }),
        select: (response) => response.data,
        queryFn: () => new RecordManagement("sys_client_script")
            .where("active", true)
            .where("for_table", tableId)
            .orWhere("for_table", "empty")
            .orderBy("order")
            .run(),
        enabled: enabled && !!tableId,
        retry: (failureCount, error) => (error as any)?.response?.status !== 403 && failureCount < 3,
    });

    const activeScripts = useMemo(
        () => scripts
            .filter((script: SysRecord) => evaluateCondition(script.conditions, context))
            .sort((first: SysRecord, second: SysRecord) => Number(first.order || 0) - Number(second.order || 0)),
        [context, scripts]
    );

    const byEvent = useMemo(
        () => ({
            onLoad: filterByEvent(activeScripts, "onLoad"),
            onSubmit: filterByEvent(activeScripts, "onSubmit"),
            onChange: filterByEvent(activeScripts, "onChange"),
        }),
        [activeScripts]
    );

    return {
        scripts: activeScripts,
        byEvent,
        isLoading,
    };
};

const filterByEvent = (scripts: SysRecord[], eventType: ClientScriptEventType) =>
    scripts.filter((script) => script.event_type === eventType);

export default useClientScripts;
